import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@roger/ui-components';
import { CheckCircle, LockOpen } from 'lucide-react';
import type { TaskSignatureResponse } from '../services/api/types.gen';
import { formatSignatureDate } from './signing/signatureDisplayDate';
import { signerName } from './signing/signatureSummary';

interface SignatureInvalidationDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  signatures: TaskSignatureResponse[];
  isLoading?: boolean;
  actionLabel?: string;
}

export const SignatureInvalidationDialog: React.FC<
  SignatureInvalidationDialogProps
> = ({
  open,
  onConfirm,
  onCancel,
  signatures,
  isLoading = false,
  actionLabel,
}) => {
  const label = actionLabel || 'Edit & Unlock';
  return (
    <Dialog
      open={open}
      onOpenChange={open => !open && !isLoading && onCancel()}
    >
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <LockOpen className='h-5 w-5 text-primary' />
            {label === 'Unsubmit & Edit'
              ? 'Unsubmit Visit for Editing?'
              : 'Unlock Visit for Editing?'}
          </DialogTitle>
          <DialogDescription className='space-y-3 pt-2 text-sm'>
            <div className='rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-foreground'>
              <p className='font-medium'>This visit is currently locked.</p>
              <p className='mt-1 text-muted-foreground'>
                {label === 'Unsubmit & Edit'
                  ? 'This will remove signatures and unsubmit the visit, returning it to Saved status for editing.'
                  : 'Unlocking will remove signatures and return the chart to editable mode.'}
              </p>
            </div>

            <div className='space-y-2 pt-1'>
              <p className='font-medium text-foreground'>
                Signatures to remove ({signatures.length}):
              </p>
              <div className='rounded-md border border-border/60 bg-background px-3 py-2 max-h-44 overflow-y-auto'>
                <div className='space-y-1.5'>
                  {signatures.map(sig => (
                    <div
                      key={sig.id}
                      className='flex items-center gap-2 text-sm text-muted-foreground'
                    >
                      <CheckCircle className='h-3.5 w-3.5 flex-shrink-0 text-primary' />
                      <span className='font-medium text-foreground'>
                        {signerName(sig)}
                      </span>
                      <span>•</span>
                      <span>{formatSignatureDate(sig)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='pt-1'>
          <Button variant='outline' onClick={onCancel} disabled={isLoading}>
            Keep Locked
          </Button>
          <Button
            variant='destructive'
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
